import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {
  Field,
  type FieldProps,
  Form,
} from 'formik'
import React, {
  type ChangeEvent,
  memo,
  useEffect,
  useMemo,
} from 'react'

import BaseAmountInput from '@/components/base/BaseAmountInput'
import Button from '@/components/base/Button'
import { HTMLCustomNumericElement } from '@/components/base/CustomNumericFormat'
import TokenPicker from '@/components/base/TokenPicker'
import BackButton from '@/components/common/BackButton'
import SetLiquidityDialog from '@/components/open-position/SetLiquidityDialog'
import useAmplitude, { EVENT_NAMES } from '@/hooks/use-amplitude'
import useTrans from '@/hooks/useTrans'
import {
  format,
  formatSafe,
  toDecimal,
} from '@/utils/numeric'

import type {
  OpenPositionFormValues,
  OpenPositionViewProps,
} from './types'

const View = (props: OpenPositionViewProps) => {
  const {
    handleTokenAmountChange,
    tokenIn,
    tokenOut,
    isSubmitting,
    submitForm,
    values,
    setPriceRange,
    maxApplicableTokenBalances,
    isValid,
    usdRateA,
    usdRateB,
    errors,
    walletConnected,
    handleInputMaximumBalance,
    pricesReverted,
    onChangeRange,
    isOpenSetLiquidity,
    formattedMinPrice,
    formattedMaxPrice,
    insufficientBalanceMsg,
  } = props
  const amplitude = useAmplitude()
  const trans = useTrans()

  const totalUsd = useMemo(() => {
    const tokenMaxValueA = toDecimal(values.tokenAmountA).times(usdRateA)
    const tokenMaxValueB = toDecimal(values.tokenAmountB).times(usdRateB)

    return tokenMaxValueA.plus(tokenMaxValueB)
  }, [usdRateA, usdRateB, values.tokenAmountA, values.tokenAmountB])

  useEffect(() => {
    if (!errors.minPrice && !errors.maxPrice && isOpenSetLiquidity) {
      setPriceRange(values.minPrice, values.maxPrice, pricesReverted)
    }
  }, [
    errors.minPrice,
    errors.maxPrice,
    values.minPrice,
    values.maxPrice,
    pricesReverted,
    setPriceRange,
    isOpenSetLiquidity,
  ])

  const endAdornment = pricesReverted ? `${tokenIn.symbol}/${tokenOut.symbol}` : `${tokenOut.symbol}/${tokenIn.symbol}`

  return (
    <Form data-testid="open-position-form" data-pricesreverted={pricesReverted}>
      <SetLiquidityDialog {...props} />
      <Stack className="h-full mt-6" justifyContent="space-between" alignItems="center" gap={1}>
        <Box className="md:w-[488px] w-full">
          <BackButton href='/liquidity/browse' />
        </Box>
        <Stack className="md:w-[488px] sm:w-full p-7 rounded-[32px] bg-[#1E2735] sm:mb-14 mb-6" spacing={2}>
          <Stack>
            <Typography className="text-xl font-bold">
              {trans.liquidity.open_position.add_new_position}
            </Typography>
          </Stack>
          <Stack justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack spacing={1} className="bg-[#222C3C] rounded-2xl px-4 py-3 w-full">
            <Stack
                data-testid="box-price-range"
                spacing={1}
                justifyContent="space-between"
                alignItems="start"
              >
                <Stack className="w-full" direction="row" justifyContent="space-between" alignItems="center">
                  <Typography className="text-sm font-normal">
                    {trans.liquidity.open_position.price_range}:
                  </Typography>
                  <Stack
                    data-testid="box-change"
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    className="relative"
                  >
                    <Typography
                      data-testid="change-liquidity-price"
                      className="text-sm underline border-b cursor-pointer border-gradient text-gradient"
                      onClick={onChangeRange}
                    >
                      {trans.liquidity.open_position.change}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row" justifyContent="end" alignItems="center" spacing={0.5}>
                  <Typography className="text-sm font-bold" data-testid="price-range">
                    {formattedMinPrice} ~ {formattedMaxPrice}
                  </Typography>
                  <Typography
                    className="text-sm text-[#B0B2BE]"
                  >
                    {endAdornment}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            <Stack justifyContent="space-between" alignItems="center" className="w-full">
              <Box
                data-testid="box-token-a-amount"
                className="w-full bg-[#141A23] rounded-2xl pt-8 px-8 pb-4"
              >
                <Stack direction="row">
                  <Stack className="flex basis-full" justifyContent="center" alignItems="start">
                    <Field name="tokenAmountA" label={tokenIn.symbol}>
                      {({ field, meta }: FieldProps<OpenPositionFormValues['tokenAmountA']>) => (
                        <BaseAmountInput
                          {...field}
                          value={field.value}
                          data-testid="input-token-a-amount"
                          onChange={(event: ChangeEvent<HTMLCustomNumericElement>) => {
                            field.onChange(event)
                            handleTokenAmountChange(
                              event.target.name as keyof OpenPositionFormValues,
                              event.target.floatValue,
                            )
                          }}
                          error={Boolean(meta.touched && meta.error)}
                        />
                      )}
                    </Field>
                  </Stack>
                  <TokenPicker data-testid="token-in" nickName="tokenA" selectedToken={tokenIn} disableDropdown />
                </Stack>
                <Stack className="mt-2">
                  <Typography variant="caption" className="text-sm text-right opacity-40">
                    {`${trans.liquidity.open_position.max_balance}: `}
                    <Typography
                      data-testid="btn-input-token-balance"
                      variant="caption"
                      onClick={handleInputMaximumBalance}
                      className="text-sm text-right cursor-pointer hover:underline"
                    >
                      {formatSafe(
                        maxApplicableTokenBalances
                          ?.get(tokenIn.symbol)
                          ?.toFixed(tokenIn.decimals) || 0,
                        tokenIn.decimals,
                      )}
                    </Typography>
                  </Typography>
                </Stack>
              </Box>
              <Box className="relative h-[4px] w-full">
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  className="absolute w-[34px] h-[34px] rounded-full bg-[#4B586A] top-[-17px] left-[50%] ml-[-17px]"
                >
                  <ExpandMoreIcon className="flex" />
                </Stack>
              </Box>
              <Box
                data-testid="box-token-b-amount"
                className="w-full bg-[#141A23] rounded-2xl pt-8 px-8 pb-4"
              >
                <Stack direction="row">
                  <Stack className="flex basis-full" justifyContent="center" alignItems="start">
                    <Field name="tokenAmountB" label={tokenOut.symbol}>
                      {({ field, meta }: FieldProps<OpenPositionFormValues['tokenAmountB']>) => (
                        <BaseAmountInput
                          {...field}
                          value={field.value}
                          data-testid="input-token-b-amount"
                          onChange={(event: ChangeEvent<HTMLCustomNumericElement>) => {
                            field.onChange(event)
                            handleTokenAmountChange(
                              event.target.name as keyof OpenPositionFormValues,
                              event.target.floatValue,
                            )
                          }}
                          error={Boolean(meta.touched && meta.error)}
                        />
                      )}
                    </Field>
                  </Stack>
                  <TokenPicker data-testid="token-out" nickName="tokenB" selectedToken={tokenOut} disableDropdown />
                </Stack>
                <Stack className="mt-2">
                  <Typography variant="caption" className="text-sm text-right opacity-40">
                    {`${trans.liquidity.open_position.max_balance}: `}
                    <Typography
                      data-testid="btn-output-token-balance"
                      variant="caption"
                      onClick={handleInputMaximumBalance}
                      className="text-sm text-right cursor-pointer hover:underline"
                    >
                      {format(
                        maxApplicableTokenBalances?.get(tokenOut.symbol),
                        tokenOut.decimals,
                      )}
                    </Typography>
                  </Typography>
                </Stack>
              </Box>
            </Stack>
            {isValid && (
              <Stack data-testid="txt-total-price" className="w-full py-3" direction="row">
                <Typography className="text-sm">{trans.liquidity.open_position.total}</Typography>
                <Typography className="ml-6 text-sm">
                  <strong data-testid="total-amount-in-usd">{format(totalUsd)}</strong>
                  <span className="ml-1 opacity-40">reUSD</span>
                </Typography>
              </Stack>
            )}
            <Stack className="w-full">
              <Button
                data-testid="btn-confirm-deposit"
                fullWidth
                size="xl"
                onClick={() => {
                  submitForm()
                  if (!walletConnected) {
                    amplitude.track(EVENT_NAMES.liquidity_deposit_connect_wallet_on_form)
                  } if (!Object.keys(errors).length) {
                    amplitude.track(EVENT_NAMES.liquidity_deposit_submit, {
                      from_token: tokenIn.symbol,
                      to_token: tokenOut.symbol,
                      min_price: values.minPrice,
                      max_price: values.maxPrice,
                      from_amount: values.tokenAmountA,
                      to_amount: values.tokenAmountB,
                    })
                  }
                }}
                disabled={!isValid || Boolean(insufficientBalanceMsg)}
                loading={isSubmitting}
              >
                {walletConnected ? (
                  insufficientBalanceMsg || trans.liquidity.open_position.add_new_position
                ) : (
                  trans.common.connect_wallet
                )}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Form>
  )
}

export default memo(View)
